import React, { useState, useEffect, useContext } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { WalletConnectContext } from '../contexts/WalletConnectContext';
import { lotteryService, LotteryInfo, UserStats, ContractStats } from '../services/lotteryService';
import { getContractAddress } from '../config/contracts';
import { ethers } from 'ethers';

const RealLotteryPage: React.FC = () => {
  const { accountId } = useContext(WalletConnectContext);
  const [contractStats, setContractStats] = useState<ContractStats | null>(null);
  const [lotteries, setLotteries] = useState<LotteryInfo[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joiningLottery, setJoiningLottery] = useState<number | null>(null);
  const [claimingPrize, setClaimingPrize] = useState<number | null>(null);

  // Load contract data
  useEffect(() => {
    loadContractData();
  }, [accountId]);

  const loadContractData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get contract stats
      const stats = await lotteryService.getContractStats();
      setContractStats(stats);

      // Load all lotteries
      const lotteryList: LotteryInfo[] = [];
      for (let i = 0; i < stats.currentLotteryId; i++) {
        try {
          const lottery = await lotteryService.getLottery(i);
          lotteryList.push(lottery);
        } catch (err) {
          console.warn(`Failed to load lottery ${i}:`, err);
        }
      }
      setLotteries(lotteryList);

      // Get user stats if connected
      if (accountId) {
        const userStats = await lotteryService.getUserStats(accountId);
        setUserStats(userStats);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contract data');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinLottery = async (lotteryId: number, entryFee: string) => {
    if (!accountId) {
      setError('Please connect your wallet first');
      return;
    }

    try {
      setJoiningLottery(lotteryId);
      setError(null);

      const txHash = await lotteryService.joinLottery(lotteryId, entryFee);
      console.log('Join lottery transaction:', txHash);

      // Reload data after successful join
      await loadContractData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join lottery');
    } finally {
      setJoiningLottery(null);
    }
  };

  const handleClaimPrize = async (lotteryId: number) => {
    if (!accountId) {
      setError('Please connect your wallet first');
      return;
    }

    try {
      setClaimingPrize(lotteryId);
      setError(null);

      const txHash = await lotteryService.claimPrize(lotteryId);
      console.log('Claim prize transaction:', txHash);

      // Reload data after successful claim
      await loadContractData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to claim prize');
    } finally {
      setClaimingPrize(null);
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const getTimeRemaining = (endTime: number) => {
    const now = Math.floor(Date.now() / 1000);
    const remaining = endTime - now;
    
    if (remaining <= 0) return 'Ended';
    
    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;
    
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const isLotteryActive = (lottery: LotteryInfo) => {
    const now = Math.floor(Date.now() / 1000);
    return lottery.isActive && now < lottery.endTime && lottery.participantCount < lottery.maxParticipants;
  };

  const canJoinLottery = (lottery: LotteryInfo) => {
    if (!accountId) return false;
    return isLotteryActive(lottery) && lottery.winner === ethers.constants.AddressZero;
  };

  const canClaimPrize = (lottery: LotteryInfo) => {
    if (!accountId) return false;
    return lottery.isEnded && lottery.winner.toLowerCase() === accountId.toLowerCase() && lottery.prizePool !== '0.0';
  };

  if (loading) {
    return (
      <div className="min-h-full bg-[#0a0a1a] text-green-400 font-mono p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl mb-4">🎰</div>
          <div className="text-green-400">Loading lottery data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#0a0a1a] text-green-400 font-mono p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-green-400 mb-2">🎰 COPYCAT Lottery</h1>
          <div className="text-green-400/70 text-sm">
            Fair and verifiable lottery powered by Pyth Entropy
          </div>
        </div>
        <Button 
          onClick={loadContractData}
          className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30"
        >
          🔄 Refresh
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="text-red-400 font-medium">Error:</div>
          <div className="text-red-400/70 text-sm">{error}</div>
          {error.includes('Contract address not configured') && (
            <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
              <div className="text-blue-400 font-medium mb-2">📋 Setup Instructions:</div>
              <div className="text-blue-400/70 text-sm space-y-1">
                <div>1. Deploy the lottery contract to Ethereum Sepolia</div>
                <div>2. Update the contract address in <code className="bg-blue-500/20 px-1 rounded">src/config/contracts.ts</code></div>
                <div>3. Refresh the page</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contract Stats */}
      {contractStats && (
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/30">
            <div className="text-green-400/70 text-sm mb-1">Total Lotteries</div>
            <div className="text-2xl font-bold text-green-400">{contractStats.totalLotteries}</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/30">
            <div className="text-blue-400/70 text-sm mb-1">Total Prizes</div>
            <div className="text-2xl font-bold text-blue-400">{contractStats.totalPrizesDistributed} ETH</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/30">
            <div className="text-purple-400/70 text-sm mb-1">Current Lottery ID</div>
            <div className="text-2xl font-bold text-purple-400">{contractStats.currentLotteryId}</div>
          </Card>
        </div>
      )}

      {/* User Stats */}
      {userStats && accountId && (
        <div className="mb-6 p-4 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/30 rounded-lg">
          <h3 className="text-green-400 font-bold mb-2">👤 Your Stats</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-green-400/70 text-sm">Total Winnings</div>
              <div className="text-green-400 font-bold">{userStats.winnings} ETH</div>
            </div>
            <div>
              <div className="text-green-400/70 text-sm">Participations</div>
              <div className="text-green-400 font-bold">{userStats.participations}</div>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Connection */}
      {!accountId && (
        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <div className="text-yellow-400 font-medium">⚠️ Wallet Not Connected</div>
          <div className="text-yellow-400/70 text-sm">
            Connect your wallet to participate in lotteries and claim prizes.
          </div>
        </div>
      )}

      {/* Lotteries List */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-green-400">🎲 Active Lotteries</h2>
        
        {lotteries.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-green-400/50 text-lg mb-2">No lotteries found</div>
            <div className="text-green-400/30 text-sm">
              Check back later for new lottery rounds
            </div>
          </div>
        ) : (
          lotteries.map((lottery) => (
            <Card key={lottery.lotteryId} className="p-6 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30 hover:border-green-400/50 transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-green-300 font-semibold mb-1 text-lg">
                    Lottery #{lottery.lotteryId}
                  </h3>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      lottery.isActive ? 'text-green-400 bg-green-400/20' : 'text-red-400 bg-red-400/20'
                    }`}>
                      {lottery.isActive ? 'ACTIVE' : 'ENDED'}
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full font-medium text-blue-400 bg-blue-400/20">
                      {lottery.participantCount}/{lottery.maxParticipants} participants
                    </span>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <div className="text-green-300 font-bold text-xl">{lottery.prizePool} ETH</div>
                  <div className="text-xs text-green-500/70">Prize Pool</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                  <div className="text-green-400/70 text-xs mb-1">Entry Fee</div>
                  <div className="text-green-400 text-sm font-medium">{lottery.entryFee} ETH</div>
                </div>
                <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                  <div className="text-green-400/70 text-xs mb-1">Time Remaining</div>
                  <div className="text-green-400 text-sm font-medium">{getTimeRemaining(lottery.endTime)}</div>
                </div>
                <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                  <div className="text-green-400/70 text-xs mb-1">Start Time</div>
                  <div className="text-green-400 text-sm">{formatTime(lottery.startTime)}</div>
                </div>
                <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                  <div className="text-green-400/70 text-xs mb-1">End Time</div>
                  <div className="text-green-400 text-sm">{formatTime(lottery.endTime)}</div>
                </div>
              </div>

              {/* Winner Display */}
              {lottery.isEnded && lottery.winner !== ethers.constants.AddressZero && (
                <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <div className="text-yellow-400 font-medium">🏆 Winner</div>
                  <div className="text-yellow-400/70 text-sm font-mono">{lottery.winner}</div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-2">
                {canJoinLottery(lottery) && (
                  <Button
                    onClick={() => handleJoinLottery(lottery.lotteryId, lottery.entryFee)}
                    disabled={joiningLottery === lottery.lotteryId}
                    className="flex-1 bg-gradient-to-r from-green-500/20 to-blue-500/20 hover:from-green-500/30 hover:to-blue-500/30 text-green-400 border border-green-500/30"
                  >
                    {joiningLottery === lottery.lotteryId ? '⏳ Joining...' : '🎫 Join Lottery'}
                  </Button>
                )}
                
                {canClaimPrize(lottery) && (
                  <Button
                    onClick={() => handleClaimPrize(lottery.lotteryId)}
                    disabled={claimingPrize === lottery.lotteryId}
                    className="flex-1 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 text-yellow-400 border border-yellow-500/30"
                  >
                    {claimingPrize === lottery.lotteryId ? '⏳ Claiming...' : '🏆 Claim Prize'}
                  </Button>
                )}

                {!canJoinLottery(lottery) && !canClaimPrize(lottery) && (
                  <div className="flex-1 text-center text-green-400/50 text-sm py-2">
                    {!accountId ? 'Connect wallet to participate' :
                     lottery.isEnded ? 'Lottery ended' : 'Not eligible'}
                  </div>
                )}
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Contract Info */}
      <div className="mt-8 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg">
        <h3 className="text-blue-400 font-bold mb-2">📋 Contract Information</h3>
        <div className="text-blue-400/70 text-sm space-y-1">
          <div>Contract: {getContractAddress('SwapLottery')}</div>
          <div>Network: Hedera Testnet</div>
          <div>Powered by: Pyth Entropy for fair randomness</div>
        </div>
      </div>
    </div>
  );
};

export default RealLotteryPage;
